import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Skeleton,
} from '../../components/ui'
import { AlertIcon } from '../../components/icons'
import { CumulativeAreaChart, RangeChips, WeeklyBarChart, WeeklyLineChart } from '../../components/charts'
import '../miner/dashboard.css'

/* Faithful port of anansi/app/templates/admin/dashboard.html.
 *
 * Reconciled against the actual GET /api/v1/admin/dashboard response
 * (api/routes.py api_admin_dashboard(), ~4571-4666): every scalar lives
 * under `kpis`, keyed as `users_total` / `users_suspended` / `orgs_total`
 * (not `active_users` / `total_orgs` as originally guessed - the Jinja
 * source names differ from the JSON API's pre-existing kpi keys). There is
 * no `active_users` field at all, so it's derived below. `by_role` is a
 * real top-level field (matches). There is NO array of multi-org user rows
 * anywhere in the response - only scalar counts (`kpis.multi_org_users` /
 * `kpis.multi_org_count`, both = len(multi_org)) - so `multiOrgUsers` below
 * is currently always empty against this endpoint; kept typed so the
 * summary card below "just works" the day the backend adds the array.
 * That card intentionally stays a one-line summary + link rather than a
 * raw per-person table - home is stats and a graph, not a data dump; the
 * people themselves are one click away in Users. */
interface AdminKpis {
  users_total?: number
  users_suspended?: number
  orgs_total?: number
  pending_approvals?: number
  pending_users?: number
  daily_safety_week?: number
  unsafe_week?: number
  incidents_open?: number
  hazards_open?: number
  production_month?: number
  loads_week?: number
  notices_open?: number
  multi_org_count?: number
}

interface MultiOrgUser {
  uid?: string
  full_name?: string
  name?: string
  email?: string
  role?: string
  org_names?: string[]
  organisations?: string[]
}

interface DashboardSeries {
  weeks: string[]
  loads: number[]
  tonnage: number[]
  incidents: number[]
  hazards: number[]
  production_kg: number[]
  users_total_series?: number[]
  new_users?: number[]
}

interface AdminDashboardResponse {
  ok?: boolean
  series?: DashboardSeries
  kpis?: AdminKpis
  by_role?: Record<string, number>
  // Not actually returned by the endpoint today (see note above) - kept so
  // the table below "just works" the day the backend adds it.
  multi_org_users?: MultiOrgUser[]
  multi_org_members?: MultiOrgUser[]
}

type Tone = 'info' | 'success' | 'warning' | 'danger'
const TONE_VAR: Record<Tone, string> = {
  info: 'var(--info)', success: 'var(--success)', warning: 'var(--warning)', danger: 'var(--danger)',
}

type Tab = 'overview' | 'operations' | 'safety'

export default function AdminDashboard() {
  const [tab, setTab] = useState<Tab>('overview')
  // Time-range chips shared by every chart on this dashboard - the backend
  // re-buckets its series over ?weeks= (clamped 4..52 server-side).
  const [weeks, setWeeks] = useState(8)
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'dashboard', weeks],
    queryFn: () => api.get<AdminDashboardResponse>(`/admin/dashboard?weeks=${weeks}`),
  })
  const rangeSub = `last ${weeks} weeks`

  const kpis = data?.kpis ?? {}
  // "Active" has no direct field on the wire - derive it the same way the
  // Jinja original's `active_users` excludes suspended/pending accounts.
  const activeUsers = kpis.users_total === undefined
    ? undefined
    : kpis.users_total - (kpis.users_suspended ?? 0) - (kpis.pending_users ?? 0)
  const k = { ...kpis, active_users: activeUsers, total_orgs: kpis.orgs_total }

  const roles = data?.by_role ?? {}
  const roleEntries = Object.entries(roles)

  const multiOrgUsers = data?.multi_org_users ?? data?.multi_org_members ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>System dashboard</h1>
          <p className="page-head__sub">Platform-wide users, organisations, and safety activity.</p>
        </div>
      </div>

      {isError && <Alert tone="danger">{(error as Error).message}</Alert>}

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={tab === 'overview' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('overview')}>
          Overview
        </Button>
        <Button variant={tab === 'operations' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('operations')}>
          Operations
        </Button>
        <Button variant={tab === 'safety' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('safety')}>
          Safety
        </Button>
      </div>

      {tab === 'overview' && (
        <>
          {/* KPI cards. Active Users / Awaiting Activation route to /users
              (this portal's Users page); Organisations routes to
              /organizations (Phase 2); Pending Approvals routes to
              /approvals (Phase 3). grid--tiles-2 (not grid--stats) so these
              stay a clean 2x2 on a phone - grid--stats' auto-fit needs
              2x190px of room, which a 390px screen minus page padding
              doesn't quite have, so every tile fell back to one full-width
              column per row (found live via a mobile screenshot). */}
          <div className="grid grid--tiles-2" style={{ marginBottom: 'var(--sp-6)' }}>
            <Tile label="Active Users" value={k.active_users} tone="info" to="/users" loading={isLoading} />
            <Tile label="Organisations" value={k.total_orgs} tone="success" to="/organizations" loading={isLoading} />
            <Tile label="Pending Approvals" value={k.pending_approvals} tone="warning" to="/approvals" loading={isLoading} />
            <Tile label="Awaiting Activation" value={k.pending_users} tone="danger" to="/users" loading={isLoading} />
          </div>

          <Card style={{ marginBottom: 'var(--sp-6)' }}>
            <CardHeader title="Users by role" sub={roleEntries.length ? `${roleEntries.length} roles in use` : undefined} />
            <CardBody>
              {isLoading ? (
                <Loading />
              ) : roleEntries.length === 0 ? (
                <EmptyState title="No users yet" />
              ) : (
                <RoleBarChart entries={roleEntries} />
              )}
            </CardBody>
          </Card>

          {/* Growth over time - the reference admin panel's "total users"
              cumulative curve plus new sign-ups per week, over the selected
              range. */}
          <Card style={{ marginBottom: 'var(--sp-6)' }}>
            <CardHeader title="Total users over time" sub={`Cumulative accounts, ${rangeSub}`} />
            <CardBody>
              <RangeChips value={weeks} onChange={setWeeks} />
              {isLoading || !data?.series?.users_total_series ? <Loading /> : (
                <CumulativeAreaChart
                  weeks={data.series.weeks}
                  values={data.series.users_total_series}
                  label="Total users over time"
                />
              )}
            </CardBody>
          </Card>

          <Card style={{ marginBottom: 'var(--sp-6)' }}>
            <CardHeader title="New users per week" sub={`Accounts created, ${rangeSub}`} />
            <CardBody>
              {isLoading || !data?.series?.new_users ? <Loading /> : (
                <WeeklyBarChart weeks={data.series.weeks} values={data.series.new_users} />
              )}
            </CardBody>
          </Card>

          {/* Raw per-person detail (who exactly is multi-org, who has an
              incomplete profile, etc.) belongs one click away, not on the
              home screen - Users / Organisations already carry that, and
              clicking into one shows its own issues. Home stays summary +
              graph. */}
          {!isLoading && multiOrgUsers.length > 0 && (
            <Link to="/users" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Card hover style={{ borderColor: 'var(--warning)' }}>
                <CardBody>
                  <div className="row" style={{ gap: 'var(--sp-2)', color: 'var(--warning)' }}>
                    <AlertIcon size={17} />
                    <strong>{multiOrgUsers.length} member{multiOrgUsers.length === 1 ? '' : 's'} belong to more than one organisation</strong>
                  </div>
                  <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                    View in Users →
                  </p>
                </CardBody>
              </Card>
            </Link>
          )}
        </>
      )}

      {tab === 'operations' && (
        <div className="stack" style={{ gap: 'var(--sp-5)' }}>
          <div className="grid grid--tiles-3">
            <Tile label="Loads (week)" value={k.loads_week} tone="success" to="/loads" loading={isLoading} />
            <Tile label="Production (month)" value={k.production_month} tone="warning" loading={isLoading} />
            <Tile label="Organisations" value={k.total_orgs} tone="info" to="/organizations" loading={isLoading} />
          </div>
          <RangeChips value={weeks} onChange={setWeeks} />
          <Card>
            <CardHeader title="Loads per week" sub={`Every load dispatched, ${rangeSub}`} />
            <CardBody>
              {isLoading || !data?.series ? <Loading /> : (
                <WeeklyLineChart weeks={data.series.weeks} series={[{ label: 'Loads', values: data.series.loads }]} />
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Tonnage per week" sub={`Total tonnes moved, ${rangeSub}`} />
            <CardBody>
              {isLoading || !data?.series ? <Loading /> : (
                <WeeklyBarChart weeks={data.series.weeks} values={data.series.tonnage} unit=" t" />
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Production per week" sub={`Volume logged (kg), ${rangeSub}`} />
            <CardBody>
              {isLoading || !data?.series ? <Loading /> : (
                <WeeklyBarChart weeks={data.series.weeks} values={data.series.production_kg} />
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {tab === 'safety' && (
        <div className="stack" style={{ gap: 'var(--sp-5)' }}>
          <div className="grid grid--tiles-3">
            <Tile label="Daily safety" value={k.daily_safety_week} tone="success" loading={isLoading} />
            <Tile label="Unsafe shifts" value={k.unsafe_week} tone="danger" loading={isLoading} />
            <Tile label="Open incidents" value={k.incidents_open} tone="danger" loading={isLoading} />
            <Tile label="Open hazards" value={k.hazards_open} tone="warning" loading={isLoading} />
          </div>
          <RangeChips value={weeks} onChange={setWeeks} />
          <Card>
            <CardHeader title="Incidents &amp; hazards per week" sub={`New reports raised, ${rangeSub}`} />
            <CardBody>
              {isLoading || !data?.series ? <Loading /> : (
                <WeeklyLineChart
                  weeks={data.series.weeks}
                  series={[
                    { label: 'Incidents', values: data.series.incidents },
                    { label: 'Hazards', values: data.series.hazards },
                  ]}
                />
              )}
            </CardBody>
          </Card>
          {!isLoading && !!k.notices_open && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--danger)' }}>
              {k.notices_open} open compliance notice(s) across organisations.
            </p>
          )}
        </div>
      )}
    </>
  )
}

/** Horizontal bar list - one hue (the brand accent), sorted descending, value
 * directly labeled at the tip per dataviz mark specs (bars ≤24px thick, 4px
 * rounded data-end, square at the baseline, text in a text token not the
 * bar's own color). A single series needs no legend - the card title already
 * says what's plotted. */
function RoleBarChart({ entries }: { entries: [string, number][] }) {
  const sorted = [...entries].sort((a, b) => b[1] - a[1])
  const max = Math.max(...sorted.map(([, n]) => n), 1)
  return (
    <div className="stack" style={{ gap: 'var(--sp-3)' }}>
      {sorted.map(([role, count]) => (
        <div key={role}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{titleCase(role)}</span>
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--text)' }}>{count}</span>
          </div>
          <div style={{ height: 10, borderRadius: 'var(--radius-pill)', background: 'var(--bg-inset)', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%', width: `${Math.max((count / max) * 100, 4)}%`,
                background: 'var(--accent)', borderRadius: 'var(--radius-pill)',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

function Loading() {
  return (
    <div className="stack">
      <Skeleton h={18} /><Skeleton h={18} w="72%" /><Skeleton h={18} w="58%" />
    </div>
  )
}

function Tile({
  label, value, tone, to, loading,
}: { label: string; value?: number; tone: Tone; to?: string; loading: boolean }) {
  const body = (
    <Card hover={!!to}>
      <div className="stat">
        <span className="stat__label">{label}</span>
        {loading ? <Skeleton h={30} w="50%" /> : (
          <div className="stat__value" style={{ color: TONE_VAR[tone] }}>{value ?? 0}</div>
        )}
      </div>
    </Card>
  )
  return to ? <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>{body}</Link> : body
}
